import { useEffect, useState, useCallback } from 'react';
import GenericDialog from '../../../components/Dialog/GenericDialog';
import SecureFileUploaderModalContent from './SecureFileUploaderModalContent';
import { createGuid } from '../../../components/Utilities/HelperMethods';
import { ProcessMultipleFiles, GetAgentLineOfBusiness } from '../../../api/SecureFileUploader';
import { useDeleteCanceledFiles } from '../Hooks/useDeleteCanceledFiles';
import { Snackbar } from '../../../components/Global/Snackbar/Snackbar';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';

const SecureFileUploaderModal = ({
  isModalOpen,
  toggleModal,
  permissions,
  producerID,
  agency,
  agentInfo,
  refreshUploadedData,
}) => {
  const queryClient = useQueryClient();
  const [submitForm, setSubmitForm] = useState(null);
  const [sessionGuid, setSessionGuid] = useState(null);
  const [hasFiles, setHasFiles] = useState(false);
  const [fileOpsInProgress, setFileOpsInProgress] = useState(false);
  const [snackbarDetails, setSnackbarDetails] = useState({
    isOpen: false,
    message: '',
    status: '',
    requiresAction: false,
  });

  const products = [
    { id: 1, value: 'WL', lobs: ['WL', 'SPWL'], label: 'Fully Underwritten (Term, WL, UL)' },
    { id: 2, value: 'WL', lobs: ['WL', 'SPWL'], label: 'Term Conversions' },
    { id: 3, value: 'WL', lobs: ['WL', 'SPWL'], label: 'aPriority (Term, WL, UL)' },
    { id: 4, value: 'MYGA', label: 'IQumulate' },
    { id: 5, value: 'GENL', label: 'Annuity' },
    { id: 6, value: 'SPIA', label: 'Asset Transfer' },
    { id: 7, value: 'SGSL', label: 'Silver Guard' },
    { id: 8, value: 'OLA', label: 'iProvide Whole Life' },
  ];

  const filteredProducts = products.filter((product) =>
    (product.lobs ?? [product.value]).some((lob) => permissions.userLOB?.includes(lob))
  );

  const processMultipleFilesMutation = useMutation({
    mutationFn: (formData) => ProcessMultipleFiles(formData)(),
    onSuccess: (res) => {
      const message = res?.Message || res?.message || 'Files uploaded successfully!';

      setSnackbarDetails({
        isOpen: true,
        message: message,
        status: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: ['GetFilesUploadedByAgencyAndStatus'],
      });
      refreshUploadedData();
      toggleModal(false);
    },
    onError: async (error, formData) => {
      processMultipleFilesMutation.reset();

      const errorMessage = error.message || 'An error occurred while uploading files';

      // if eApp, don't delete files or close modal as it could be a Giact verification error. To Do: separate out giact call to differentiate between full error or email validation error
      if (formData.fileType === 'eApp') {
        setSnackbarDetails({
          isOpen: true,
          message: errorMessage,
          status: 'error',
        });
        return;
      }

      // otherwise, cancel the requests and delete temp files
      await deleteCanceledFilesMutation.mutateAsync(sessionGuid);

      toggleModal(false);
      setSnackbarDetails({
        isOpen: true,
        message: errorMessage,
        status: 'error',
        requiresAction: true,
      });
    },
  });

  const deleteCanceledFilesMutation = useDeleteCanceledFiles(
    (message, res) => {
      setSnackbarDetails({
        isOpen: true,
        message: message,
        status: 'success',
      });
    },
    (errorMessage, error) => {
      setSnackbarDetails({
        isOpen: true,
        message: errorMessage,
        status: 'error',
      });
    }
  );

  // grab isMutating state to pass to modal to set inputs to disabled when these are fetching
  const isMutating =
    processMultipleFilesMutation.isLoading || deleteCanceledFilesMutation.isLoading;

  // create guid token on modal open, simple state
  useEffect(() => {
    setSessionGuid(createGuid());
    setHasFiles(false);
  }, [isModalOpen]);

  const handleFormSubmit = useCallback(
    (formData) => {
      const { files, producerFilter, ...submitData } = formData;

      let selectedProduct;
      if (submitData.fileType === 'App') {
        selectedProduct = products.find((p) => p.id === formData.underwriteType);
      }

      processMultipleFilesMutation.mutate({
        ...submitData,
        overrideHome: formData.overrideHome ? 1 : 0,
        underwriteType: selectedProduct?.value ? selectedProduct.value : '',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [processMultipleFilesMutation]
  );

  const handleConfirm = () => {
    if (submitForm) {
      submitForm();
    }
  };

  const handleCancel = async () => {
    if (hasFiles) {
      await deleteCanceledFilesMutation.mutateAsync(sessionGuid);
    }
    setSessionGuid(null);
    setHasFiles(false);
    toggleModal(false);
  };

  return (
    <>
      <GenericDialog
        open={isModalOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleCancel();
        }}
        showCloseIcon={false}
        title={'Upload Files'}
        fullWidth={true}
        maxWidth={'md'}
        onConfirm={handleConfirm}
        cancelLoading={deleteCanceledFilesMutation.isLoading}
        confirmLoading={processMultipleFilesMutation.isLoading}
        disableConfirm={fileOpsInProgress}
        disableCancel={fileOpsInProgress}
        content={
          <SecureFileUploaderModalContent
            onSubmit={handleFormSubmit}
            setSubmitForm={setSubmitForm}
            permissions={permissions}
            producerID={producerID}
            agency={agency}
            agentInfo={agentInfo}
            sessionGuid={sessionGuid}
            handleCancel={handleCancel}
            setHasFiles={setHasFiles}
            isDisabled={isMutating}
            products={filteredProducts}
            setFileOpsStatus={setFileOpsInProgress}
          />
        }
      />
      <Snackbar
        open={snackbarDetails.isOpen}
        message={snackbarDetails.message}
        status={snackbarDetails.status}
        requiresAction={snackbarDetails.requiresAction}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={() =>
          setSnackbarDetails((prev) => ({
            ...prev,
            isOpen: false,
          }))
        }
      />
    </>
  );
};

export default SecureFileUploaderModal;
